import React, { useEffect } from 'react'

import ComponentLoader from '@scoir/components-core/dist/components/ComponentLoader'
import qs from '@scoir/query-string'
import { head, isEmpty } from 'lodash-es'

import api, { jsonOptions } from 'common/api'
import { getTrackEventsSessionId } from 'common/get-track-events-session-id'
import createQueryParams from 'common/use-query-params'
import { MESSAGE_KEY, VALID_REDIRECT_URIS } from 'components/SSOButtons/LoginWithCleverButton'

import StudentMatch from './StudentMatch'

const useQueryParams = createQueryParams('state', 'code')

const GOOGLE_REQUIRED = 'googleRequired'

// The callback answers 409 for three different refusals
const getErrorCode = async (err) => {
  if (!(err instanceof Response)) return ''
  return (await err.json().catch(() => null))?.code ?? ''
}

export const CleverSignUpRoute = () => {
  const [matchedStudent, setMatchedStudent] = React.useState({})
  const { state, code } = useQueryParams()
  const targetOrigin = `${window.location.protocol}//${document.domain}`
  const sessionId = getTrackEventsSessionId()
  const authorizeUser = React.useCallback(
    (creds) => {
      window.opener.postMessage(
        {
          success: true,
          key: MESSAGE_KEY,
          accessToken: creds.access_token,
        },
        targetOrigin,
      )
      window.close()
    },
    [targetOrigin],
  )

  useEffect(() => {
    const redirect_uri = `${window.location.protocol}//${window.location.host}/${VALID_REDIRECT_URIS.cleverLink}`
    const query = qs.stringify({ state, code, redirect_uri, sessionId })
    api
      .post(`/api/oauth/callback/clever?${query}`)
      .then((match) => {
        const isRegisteredUser = match.access_token && match.UserId && !isEmpty(match.UserTypes)
        if (isRegisteredUser) {
          authorizeUser(match)
        } else if (head(match.UserTypes) === 'Student') {
          setMatchedStudent(match)
        } else {
          window.opener.postMessage(
            {
              success: false,
              key: MESSAGE_KEY,
            },
            targetOrigin,
          )
          window.close()
        }
      })
      .catch(async (err) => {
        // A bare failure would dead-end; the opener can follow a redirect
        if ((await getErrorCode(err)) === GOOGLE_REQUIRED) {
          window.opener.postMessage(
            {
              key: MESSAGE_KEY,
              redirect: `/signin?errorCode=${GOOGLE_REQUIRED}`,
            },
            targetOrigin,
          )
          window.close()
          return
        }

        window.opener.postMessage(
          {
            success: false,
            key: MESSAGE_KEY,
          },
          targetOrigin,
        )
        window.close()
      })
  }, [state, code, targetOrigin, authorizeUser, sessionId])

  const confirmLink = () => {
    const student = {
      ...matchedStudent,
      Id: matchedStudent.UserId,
    }
    delete student.UserId
    const query = qs.stringify({ state, sessionId })
    api
      .post(`/api/oauth/confirm/clever?${query}`, student, jsonOptions)
      .then((creds) => authorizeUser(creds))
      .catch(() => {
        window.opener.postMessage(
          {
            success: false,
            key: MESSAGE_KEY,
          },
          targetOrigin,
        )
        window.close()
      })
  }

  return (
    <ComponentLoader loaded={!isEmpty(matchedStudent)}>
      <StudentMatch
        student={matchedStudent}
        onSignUp={confirmLink}
      />
    </ComponentLoader>
  )
}

export default CleverSignUpRoute
